import { ARG_SUBDIVISIONS } from "./arg";
import { BRA_SUBDIVISIONS } from "./bra";
import { CHL_SUBDIVISIONS } from "./chl";
import { COL_SUBDIVISIONS } from "./col";
import { MEX_SUBDIVISIONS } from "./mex";
import { PER_SUBDIVISIONS } from "./per";
import type { Subdivision, SubdivisionCatalog } from "./types";

export type { Subdivision, SubdivisionCatalog };

/**
 * Structurally identical to `ComboboxOption`, declared here so this data module keeps no dependency on the
 * form components — the same reason `document-types.ts` returns codes instead of select items.
 */
export type SubdivisionOption = { value: string; label: string };

/**
 * Keyed by ISO 3166-1 alpha-3, the code the identity forms' country selectors carry.
 *
 * The whole catalog is 158 rows (~2 KB gzipped), so it is imported statically like every other data catalog
 * in the repo. Splitting it behind a dynamic `import()` would buy a loading state in a dropdown that should
 * feel instant; the one-file-per-country layout is what makes that a localized change if the country list
 * ever grows to full ISO coverage.
 */
const SUBDIVISIONS: Readonly<Record<string, SubdivisionCatalog>> = {
  ARG: ARG_SUBDIVISIONS,
  BRA: BRA_SUBDIVISIONS,
  CHL: CHL_SUBDIVISIONS,
  COL: COL_SUBDIVISIONS,
  MEX: MEX_SUBDIVISIONS,
  PER: PER_SUBDIVISIONS,
};

const NO_SUBDIVISIONS: SubdivisionCatalog = [];
const NO_OPTIONS: readonly SubdivisionOption[] = [];

/** Built once per country and then served from these maps, so the arrays keep a stable identity. */
const optionsMemo = new Map<string, readonly SubdivisionOption[]>();
const valuesMemo = new Map<string, ReadonlySet<string>>();
const haystackMemo = new Map<string, ReadonlyMap<string, readonly string[]>>();
const filterMemo = new Map<string, (itemValue: string, query: string) => boolean>();

/**
 * Lowercase, accent-free words — so "nariño" matches Nariño, "sao paulo" matches São Paulo, and punctuation
 * in "San Andrés, Providencia y Santa Catalina" does not glue itself to a word.
 */
const tokenize = (raw: string): string[] =>
  raw
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/** What the user reads. Falls back to the submitted name when the row needs no disambiguation. */
const labelOf = (row: Subdivision): string => row.displayName ?? row.name;

export const subdivisionsFor = (iso3: string | undefined): SubdivisionCatalog =>
  (iso3 ? SUBDIVISIONS[iso3] : undefined) ?? NO_SUBDIVISIONS;

/**
 * Whether a country can be offered as a list at all. `false` means the caller must fall back to free text —
 * the backend field has no catalog behind it, so a country we have no data for must stay fillable.
 */
export const hasSubdivisions = (iso3: string | undefined): boolean => subdivisionsFor(iso3).length > 0;

export const subdivisionOptionsFor = (iso3: string | undefined): readonly SubdivisionOption[] => {
  if (!iso3) return NO_OPTIONS;
  const cached = optionsMemo.get(iso3);
  if (cached) return cached;

  const rows = subdivisionsFor(iso3);
  if (rows.length === 0) return NO_OPTIONS;

  const options = rows.map((row) => ({ value: row.name, label: labelOf(row) }));
  optionsMemo.set(iso3, options);
  return options;
};

/**
 * Whether a stored value still belongs to a country's catalog. Used to decide if a value survives a country
 * change; a value typed before this feature existed simply reads as unknown and is cleared.
 */
export const isKnownSubdivision = (iso3: string | undefined, value: string): boolean => {
  if (!iso3 || !value) return false;
  let values = valuesMemo.get(iso3);
  if (!values) {
    const rows = subdivisionsFor(iso3);
    if (rows.length === 0) return false;
    values = new Set(rows.map((row) => row.name));
    valuesMemo.set(iso3, values);
  }
  return values.has(value);
};

/**
 * Searchable words per row: the name, the display name and the aliases. The ISO code is deliberately absent —
 * it is never shown, and including it would make "br" match all 27 Brazilian states.
 */
const haystackFor = (iso3: string): ReadonlyMap<string, readonly string[]> => {
  const cached = haystackMemo.get(iso3);
  if (cached) return cached;

  const index = new Map<string, readonly string[]>();
  for (const row of subdivisionsFor(iso3)) {
    index.set(row.name, tokenize([row.name, row.displayName, ...(row.aliases ?? [])].join(" ")));
  }
  haystackMemo.set(iso3, index);
  return index;
};

/**
 * The filter to hand `ColocaComboboxField`. The built-in one matches the label only, which would leave the
 * aliases unreachable — nobody finds Ciudad de México by typing "CDMX", or São Paulo by typing "SP".
 *
 * Matching is **prefix-per-word**, not substring, and every word of the query must find a word to start. A
 * plain substring match is unusable at this scale: "SP" would also return Espírito Santo, and "es" would
 * return most of Brazil. Prefix matching keeps a two-letter UF meaning exactly one state, while "rio grande
 * do s" still narrows to Rio Grande do Sul.
 *
 * Memoized per country because it is passed as a prop: a fresh closure per render would change the combobox's
 * `filter` identity on every keystroke.
 */
export const subdivisionFilterFor = (
  iso3: string | undefined,
): ((itemValue: string, query: string) => boolean) | undefined => {
  if (!iso3 || !hasSubdivisions(iso3)) return undefined;
  const cached = filterMemo.get(iso3);
  if (cached) return cached;

  const index = haystackFor(iso3);
  const filter = (itemValue: string, query: string): boolean => {
    const needles = tokenize(query);
    if (needles.length === 0) return true;
    const words = index.get(itemValue) ?? tokenize(itemValue);
    return needles.every((needle) => words.some((word) => word.startsWith(needle)));
  };
  filterMemo.set(iso3, filter);
  return filter;
};
